import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
    Flame, Coins, Users, Droplets, TrendingUp,
    Sparkles, Lock, Layers, Trophy, ArrowRight, Radar,
} from "lucide-react";
import { formatEther } from "viem";
import { useTokenStats } from "@/hooks/useTokenStats";
import { useMintInfo } from "@/hooks/useMint";
import { fmtCompact, fmtPct } from "@/lib/format";
import { PROTOCOL, IS_TESTNET } from "@/lib/config";
import { CountUp } from "@/components/CountUp";
import { SpotlightCard } from "@/components/SpotlightCard";
import { TiltCard } from "@/components/TiltCard";
import { PiggyMark, EthMark } from "@/components/Logo";
import { ContractAddressBar } from "@/components/ContractAddressBar";

const fadeUp = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};

export default function Landing() {
    const { stats, live } = useTokenStats();

    return (
        <div className="overflow-hidden">
            <Hero stats={stats} live={live} />
            <LiveStats stats={stats} live={live} />
            <SixPaths />
            <Distribution />
            <NFTShowcase />
            <HallTeaser />
            <RadarTeaser />
            <FinalCTA />
        </div>
    );
}

// ============================================================================
// HERO
// ============================================================================

function Hero({ stats, live }: { stats: ReturnType<typeof useTokenStats>["stats"]; live: boolean }) {
    return (
        <section className="relative pt-20 md:pt-32 pb-24 md:pb-40 overflow-hidden">
            <div className="absolute inset-0 grid-bg opacity-30 [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_70%)]" />
            <div className="absolute top-40 left-1/4 w-96 h-96 bg-piggy-pink-500/20 rounded-full blur-3xl animate-drift" />
            <div className="absolute top-60 right-1/4 w-80 h-80 bg-piggy-gold-500/10 rounded-full blur-3xl animate-drift" style={{ animationDelay: "5s" }} />

            <FloatingPiggies />

            <div className="relative max-w-7xl mx-auto px-6 text-center">
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.15 }}
                    className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-pink mb-8"
                >
                    <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-piggy-pink-400 opacity-75" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-piggy-pink-500" />
                    </span>
                    <span className="text-sm font-medium text-piggy-pink-200">
                        {live
                            ? `Live on Robinhood Chain ${IS_TESTNET ? "Testnet" : "mainnet"}`
                            : "Launching on Robinhood Chain"}
                    </span>
                </motion.div>

                <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.1 }}
                    className="display text-6xl md:text-8xl lg:text-9xl font-black tracking-tighter leading-[0.95] mb-6"
                >
                    Six earning paths.
                    <span className="flex items-center justify-center gap-3 md:gap-5 mt-1">
                        <span className="gradient-text-shimmer">One piggy.</span>
                        <img
                            src="/piggy-classic.png"
                            alt="Safe Piggy mascot"
                            className="w-16 h-16 md:w-24 md:h-24 lg:w-28 lg:h-28 object-contain shrink-0 animate-float-slow drop-shadow-[0_0_35px_rgba(204,255,0,0.45)]"
                            draggable={false}
                        />
                    </span>
                </motion.h1>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.2 }}
                    className="max-w-2xl mx-auto text-lg md:text-xl text-piggy-cream/60 leading-relaxed mb-10"
                >
                    A deflationary tax token where every swap funds rewards in ETH —
                    everyone in the merry band gets a share. Hold, stake, provide LP,
                    mint, refer, or just sit back and watch the supply burn.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.3 }}
                    className="flex flex-wrap items-center justify-center gap-3"
                >
                    <Link to="/swap" className="btn-primary group">
                        Buy PIGGY
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                    </Link>
                    <Link to="/mint" className="btn-gold">
                        <Sparkles className="w-4 h-4" />
                        Mint a Piggy
                    </Link>
                    <Link to="/stake" className="btn-ghost">
                        Earn ETH
                    </Link>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.4 }}
                    className="mt-8 flex justify-center"
                >
                    <ContractAddressBar />
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.5 }}
                    className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto"
                >
                    <HeroStat label="Price"      rawValue={stats.priceUsd}      format={(n) => `$${n.toFixed(7)}`} />
                    <HeroStat label="Market Cap" rawValue={stats.marketCapUsd}  format={(n) => `$${fmtCompact(n)}`} />
                    <HeroStat label="Burned"     rawValue={stats.burnedPct * 100} format={(n) => fmtPct(n, 2)} accent />
                    <HeroStat label="24h Volume" rawValue={stats.volume24hUsd}  format={(n) => `$${fmtCompact(n)}`} />
                </motion.div>
            </div>
        </section>
    );
}

// Scattered NFT-art backdrop for the hero. Each tile is absolutely placed,
// feathered with a radial alpha mask, blurred a touch, and float-animated on
// its own delay so the field doesn't beat in unison. Hover lifts opacity and
// drops the blur — gentle reveal that doesn't fight the foreground text.
const FLOATERS: ReadonlyArray<{
    src: string; size: number; pos: string; rot: number; delay: string;
}> = [
    { src: "/nft-img/sample-piglet.png",       size: 200, pos: "top-[6%]  left-[4%]",   rot: -8,  delay: "0s"   },
    { src: "/nft-img/sample-yearling.png",     size: 240, pos: "top-[10%] right-[6%]",  rot: 7,   delay: "1.2s" },
    { src: "/nft-img/sample-hog.png",          size: 180, pos: "top-[44%] left-[10%]",  rot: 4,   delay: "0.6s" },
    { src: "/nft-img/sample-razorback.png",    size: 220, pos: "top-[40%] right-[8%]",  rot: -6,  delay: "1.8s" },
    { src: "/nft-img/sample-royal_sow.png",    size: 200, pos: "bottom-[12%] left-[14%]", rot: -3, delay: "2.4s" },
    { src: "/nft-img/sample-cosmic_piggy.png", size: 260, pos: "bottom-[8%]  right-[12%]", rot: 5, delay: "0.3s" },
];

function FloatingPiggies() {
    return (
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
            {FLOATERS.map((f, i) => (
                <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0.85 }}
                    animate={{ opacity: 0.18, scale: 1 }}
                    transition={{
                        duration: 1.2,
                        ease: [0.16, 1, 0.3, 1],
                        delay: 0.15 * i,
                    }}
                    className={`absolute ${f.pos} hidden sm:block`}
                    style={{ width: f.size, height: f.size, transform: `rotate(${f.rot}deg)` }}
                >
                    <div className="animate-float-slow w-full h-full" style={{ animationDelay: f.delay }}>
                        <img
                            src={f.src}
                            alt=""
                            draggable={false}
                            // Feathered edges via radial alpha mask + soft blur.
                            // Hover hands the user a peek by raising opacity and
                            // easing off the blur — pointer-events stay disabled
                            // on the parent so this is purely a visual hint.
                            className="w-full h-full object-cover rounded-3xl blur-[2px] opacity-90 transition-all duration-500 hover:blur-0 hover:opacity-100 pointer-events-auto"
                            style={{
                                WebkitMaskImage: "radial-gradient(circle, black 55%, transparent 100%)",
                                maskImage: "radial-gradient(circle, black 55%, transparent 100%)",
                            }}
                        />
                    </div>
                </motion.div>
            ))}
        </div>
    );
}

function HeroStat({
    label, rawValue, format, accent,
}: {
    label: string; rawValue: number; format: (n: number) => string; accent?: boolean;
}) {
    return (
        <div className="glass rounded-2xl px-5 py-4">
            <div className="text-xs text-piggy-cream/40 uppercase tracking-wider mb-1">{label}</div>
            <div className={`display text-2xl font-bold ${accent ? "gradient-text" : "text-piggy-cream"}`}>
                <CountUp value={rawValue} format={format} />
            </div>
        </div>
    );
}

// ============================================================================
// LIVE STATS
// ============================================================================

function LiveStats({ stats, live }: { stats: ReturnType<typeof useTokenStats>["stats"]; live: boolean }) {
    return (
        <section className="py-20 md:py-32 border-y border-white/[0.06] bg-white/[0.01]">
            <div className="max-w-7xl mx-auto px-6">
                <motion.div {...fadeUp} className="flex items-baseline justify-between mb-10 flex-wrap gap-4">
                    <h2 className="display text-4xl md:text-5xl font-black">
                        The piggy <span className="gradient-text">in numbers</span>
                    </h2>
                    <div className="flex items-center gap-2 text-sm text-piggy-cream/40">
                        <span className={`w-2 h-2 rounded-full ${live ? "bg-green-400" : "bg-piggy-cream/30"}`} />
                        {live ? "Live from Robinhood Chain" : "Demo data"}
                    </div>
                </motion.div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    <StatCard icon={<Coins />}      label="Total Supply"  rawValue={stats.totalSupply}     format={(n) => fmtCompact(n)}                unit="PIGGY" />
                    <StatCard icon={<Flame />}      label="Burned"        rawValue={stats.burned}          format={(n) => fmtCompact(n)}                unit="PIGGY" accent />
                    <StatCard icon={<Users />}      label="Holders"       rawValue={stats.holders}         format={(n) => fmtCompact(n, 0)}             unit="wallets" />
                    <StatCard icon={<Droplets />}   label="Liquidity"     rawValue={stats.liquidityUsd}    format={(n) => `$${fmtCompact(n)}`} />
                    <StatCard icon={<TrendingUp />} label="Market Cap"    rawValue={stats.marketCapUsd}    format={(n) => `$${fmtCompact(n)}`} />
                    <StatCard icon={<Layers />}     label="24h Volume"    rawValue={stats.volume24hUsd}    format={(n) => `$${fmtCompact(n)}`} />
                </div>
            </div>
        </section>
    );
}

function StatCard({
    icon, label, rawValue, format, unit, accent,
}: {
    icon: React.ReactNode;
    label: string;
    rawValue: number;
    format: (n: number) => string;
    unit?: string;
    accent?: boolean;
}) {
    return (
        <motion.div
            {...fadeUp}
            className={`glass rounded-2xl p-5 ${accent ? "ring-1 ring-piggy-pink-500/30 shadow-glow" : ""}`}
        >
            <div className={`w-9 h-9 rounded-xl grid place-items-center mb-3 ${accent ? "bg-piggy-pink-500/20 text-piggy-pink-300" : "bg-white/[0.04] text-piggy-cream/60"}`}>
                {icon}
            </div>
            <div className="text-xs text-piggy-cream/40 uppercase tracking-wider mb-1">{label}</div>
            <div className={`display text-2xl font-bold ${accent ? "gradient-text" : "text-piggy-cream"}`}>
                <CountUp value={rawValue} format={format} />
            </div>
            {unit && (
                <div className="text-xs text-piggy-cream/40 mt-0.5 inline-flex items-center gap-1.5">
                    {unit === "PIGGY" && <PiggyMark size={16} className="rounded-full" />}
                    {unit === "ETH" && <EthMark size={16} />}
                    <span>{unit}</span>
                </div>
            )}
        </motion.div>
    );
}

// ============================================================================
// SIX PATHS
// ============================================================================

const paths = [
    {
        icon: <Lock />,
        title: "LP Stakers",
        share: "30%",
        body: "Provide liquidity, lock for up to 180 days, multiply your share up to 2× plus NFT boosts.",
        gradient: "from-piggy-pink-400 to-piggy-pink-700",
    },
    {
        icon: <Coins />,
        title: "PIGGY Stakers",
        share: "20%",
        body: "Single-sided staking. No lockup, no IL. Just commit and earn ETH pro-rata.",
        gradient: "from-piggy-pink-500 to-piggy-gold-500",
    },
    {
        icon: <Sparkles />,
        title: "Reflection",
        share: "15%",
        body: "Hold PIGGY in your wallet. Earn ETH automatically based on your balance.",
        gradient: "from-piggy-gold-400 to-piggy-gold-600",
    },
    {
        icon: <Users />,
        title: "Referrers",
        share: "15%",
        body: "Bring buyers. Earn ETH from every taxed swap they make. NFT badges boost your share.",
        gradient: "from-piggy-pink-300 to-piggy-pink-600",
    },
    {
        icon: <Flame />,
        title: "Burn (everyone)",
        share: "10%",
        body: "Every swap permanently destroys PIGGY. Scarcer supply means every holder benefits.",
        gradient: "from-piggy-pink-500 to-red-500",
    },
    {
        icon: <Trophy />,
        title: "Treasury",
        share: "10%",
        body: "Algorithmic, on-chain, throttled by inflow/outflow. No team wallet, no slush fund.",
        gradient: "from-piggy-gold-300 to-piggy-pink-500",
    },
];

function SixPaths() {
    return (
        <section className="py-24 md:py-40">
            <div className="max-w-7xl mx-auto px-6">
                <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-16">
                    <div className="inline-block px-3 py-1 rounded-full glass text-xs font-semibold text-piggy-cream/60 mb-4">
                        EVERY SWAP. 4% TAX. SIX BUCKETS.
                    </div>
                    <h2 className="display text-5xl md:text-6xl font-black mb-5 tracking-tighter">
                        Pick your path
                        <span className="gradient-text"> — or take them all.</span>
                    </h2>
                    <p className="text-piggy-cream/60 text-lg">
                        Tax fires on any DEX. Routes auto-convert PIGGY to ETH.
                        Distributes across six paths every block. Claim what you've earned, any time.
                    </p>
                </motion.div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {paths.map((p, i) => (
                        <motion.div
                            key={p.title}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-50px" }}
                            transition={{ duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                        >
                            <SpotlightCard className="glass rounded-3xl p-7 h-full hover:bg-white/[0.04] transition-colors">
                                <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${p.gradient} grid place-items-center text-black shadow-glow mb-5 transition-transform`}>
                                    {p.icon}
                                </div>
                                <div className="flex items-baseline justify-between mb-3">
                                    <h3 className="display text-2xl font-bold text-piggy-cream">{p.title}</h3>
                                    <span className="display text-2xl font-black gradient-text">{p.share}</span>
                                </div>
                                <p className="text-piggy-cream/60 leading-relaxed text-sm">{p.body}</p>
                            </SpotlightCard>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ============================================================================
// DISTRIBUTION VISUALIZATION
// ============================================================================

function Distribution() {
    const dist = PROTOCOL.distribution;
    const entries = [
        { ...dist.lp,         color: "bg-piggy-pink-500" },
        { ...dist.pStake,     color: "bg-piggy-pink-400" },
        { ...dist.reflection, color: "bg-piggy-gold-500" },
        { ...dist.referrals,  color: "bg-piggy-gold-400" },
        { ...dist.burn,       color: "bg-red-500" },
        { ...dist.treasury,   color: "bg-piggy-violet" },
    ];

    return (
        <section className="py-24 md:py-32 border-y border-white/[0.06]">
            <div className="max-w-5xl mx-auto px-6">
                <motion.div {...fadeUp} className="text-center mb-12">
                    <h2 className="display text-4xl md:text-5xl font-black mb-4">
                        Where every taxed swap goes
                    </h2>
                    <p className="text-piggy-cream/60 max-w-2xl mx-auto">
                        Every PIGGY of tax splits across the six buckets below — the chart shows
                        the ratio, not a threshold. Conversion fires when accumulated tax hits
                        roughly <span className="text-piggy-cream">0.1% of LP depth</span> (randomized
                        0.08-0.12%), which is typically every few minutes once volume is healthy.
                    </p>
                </motion.div>

                <motion.div {...fadeUp} className="glass rounded-3xl p-8">
                    <div className="flex h-3 rounded-full overflow-hidden mb-8">
                        {entries.map((e) => (
                            <div key={e.label} className={e.color} style={{ width: `${e.bps / 100}%` }} />
                        ))}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {entries.map((e) => (
                            <div key={e.label} className="flex items-center gap-3">
                                <div className={`w-3 h-3 rounded-sm ${e.color}`} />
                                <div>
                                    <div className="display font-bold text-piggy-cream">{e.bps / 100}%</div>
                                    <div className="text-xs text-piggy-cream/50">{e.label}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </motion.div>
            </div>
        </section>
    );
}

// ============================================================================
// NFT SHOWCASE
// ============================================================================

const rarities = [
    { name: "Piglet",       count: 3000, boost: "+0.05×", img: "/nft-img/sample-piglet.png",       color: "from-pink-200 to-pink-400",     glow: "" },
    { name: "Yearling",     count: 1250, boost: "+0.10×", img: "/nft-img/sample-yearling.png",     color: "from-pink-300 to-pink-500",     glow: "" },
    { name: "Hog",          count: 500,  boost: "+0.15×", img: "/nft-img/sample-hog.png",          color: "from-pink-400 to-pink-600",     glow: "" },
    { name: "Razorback",    count: 200,  boost: "+0.20×", img: "/nft-img/sample-razorback.png",    color: "from-pink-500 to-pink-700",     glow: "shadow-glow" },
    { name: "Royal Sow",    count: 45,   boost: "+0.30×", img: "/nft-img/sample-royal_sow.png",    color: "from-piggy-gold-400 to-piggy-pink-700", glow: "shadow-gold" },
    { name: "Cosmic Piggy", count: 5,    boost: "+0.50×", img: "/nft-img/sample-cosmic_piggy.png", color: "from-piggy-pink-300 via-piggy-gold-400 to-piggy-violet", glow: "shadow-glow" },
];

function NFTShowcase() {
    const { info } = useMintInfo();
    return (
        <section className="py-24 md:py-40">
            <div className="max-w-7xl mx-auto px-6">
                <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-16">
                    <div className="inline-block px-3 py-1 rounded-full glass text-xs font-semibold text-piggy-cream/60 mb-4">
                        5,000 PIGGIES. 6 RARITIES. ON-CHAIN BOOST.
                    </div>
                    <h2 className="display text-5xl md:text-6xl font-black mb-5 tracking-tighter">
                        Enter the <span className="gradient-text">Hall of Piggys</span>
                    </h2>
                    <p className="text-piggy-cream/60 text-lg">
                        Mint a Piglet. Maybe roll a Cosmic Piggy. Every NFT boosts your LP staking multiplier
                        on top of your lock tier. Mint revenue: 50% buy-and-burn, 30% treasury, 20% artist.
                    </p>
                </motion.div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    {rarities.map((r, i) => (
                        <motion.div
                            key={r.name}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: i * 0.08 }}
                        >
                            <TiltCard>
                                <div className={`glass rounded-2xl p-5 ${r.glow}`}>
                                    <div className={`aspect-square rounded-xl overflow-hidden mb-4 bg-gradient-to-br ${r.color} shadow-inner`}>
                                        <img
                                            src={r.img}
                                            alt={`${r.name} sample`}
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                            draggable={false}
                                        />
                                    </div>
                                    <div className="display text-lg font-bold mb-1 text-piggy-cream">{r.name}</div>
                                    <div className="flex items-baseline justify-between">
                                        <span className="text-xs text-piggy-cream/40">{r.count} max</span>
                                        <span className="text-xs font-semibold text-piggy-gold-400">{r.boost}</span>
                                    </div>
                                </div>
                            </TiltCard>
                        </motion.div>
                    ))}
                </div>

                <motion.div {...fadeUp} className="text-center mt-12">
                    <Link to="/mint" className="btn-primary">
                        <Sparkles className="w-4 h-4" />
                        Mint at {formatEther(info.mintPrice)} ETH
                    </Link>
                </motion.div>
            </div>
        </section>
    );
}

// ============================================================================
// HALL TEASER
// ============================================================================

function HallTeaser() {
    const leaders = [
        { addr: "0x7f3…2a91", refs: 1247, badge: "Razorback" },
        { addr: "0x9c1…b04e", refs: 892,  badge: "Razorback" },
        { addr: "0xa84…fc52", refs: 421,  badge: "Hog" },
        { addr: "0x412…7d18", refs: 287,  badge: "Hog" },
        { addr: "0xee2…91a7", refs: 184,  badge: "Yearling" },
    ];

    return (
        <section className="py-24 md:py-32 border-y border-white/[0.06] bg-white/[0.01]">
            <div className="max-w-5xl mx-auto px-6">
                <motion.div {...fadeUp} className="flex items-end justify-between mb-10 flex-wrap gap-4">
                    <div>
                        <h2 className="display text-4xl md:text-5xl font-black mb-2">
                            Hall of <span className="gradient-text">Piggys</span>
                        </h2>
                        <p className="text-piggy-cream/60">Legends of the Hood — top referrers this season. Earn badges, boost your share.</p>
                    </div>
                    <Link to="/hall" className="btn-ghost">
                        Full leaderboard
                        <ArrowRight className="w-4 h-4" />
                    </Link>
                </motion.div>

                <motion.div {...fadeUp} className="glass rounded-3xl overflow-hidden">
                    {leaders.map((l, i) => (
                        <div
                            key={l.addr}
                            className="flex items-center gap-4 px-6 py-4 border-b border-white/[0.04] last:border-b-0 hover:bg-white/[0.02]"
                        >
                            <div className={`display text-2xl font-black w-10 ${i === 0 ? "gradient-text" : "text-piggy-cream/30"}`}>
                                {(i + 1).toString().padStart(2, "0")}
                            </div>
                            <div className="flex-1">
                                <div className="font-mono text-sm text-piggy-cream">{l.addr}</div>
                                <div className="text-xs text-piggy-cream/40 mt-0.5">{l.badge} badge</div>
                            </div>
                            <div className="text-right">
                                <div className="display font-bold text-piggy-cream">{l.refs}</div>
                                <div className="text-xs text-piggy-cream/40">referrals</div>
                            </div>
                        </div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
}

// ============================================================================
// RADAR TEASER
// ============================================================================

function RadarTeaser() {
    return (
        <section className="py-16 md:py-24">
            <div className="max-w-5xl mx-auto px-6">
                <motion.div {...fadeUp}>
                    <Link
                        to="/radar"
                        className="group glass-pink rounded-3xl p-6 md:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5 hover:bg-white/[0.04] transition-colors"
                    >
                        <div className="w-12 h-12 rounded-2xl grid place-items-center bg-piggy-pink-500/20 text-piggy-pink-300 flex-shrink-0">
                            <Radar className="w-6 h-6" />
                        </div>
                        <div className="flex-1">
                            <div className="text-xs font-semibold uppercase tracking-wider text-piggy-pink-200 mb-1">
                                New · Free tool
                            </div>
                            <h3 className="display text-2xl md:text-3xl font-black mb-1">
                                Scan Robinhood Chain tokens for red flags
                            </h3>
                            <p className="text-sm text-piggy-cream/60">
                                Piggy Radar screens new listings and shows a transparent risk
                                breakdown — the facts, not a promise. Do your own research.
                            </p>
                        </div>
                        <div className="inline-flex items-center gap-1.5 text-piggy-pink-200 font-semibold text-sm flex-shrink-0 group-hover:gap-2.5 transition-all">
                            Open Radar
                            <ArrowRight className="w-4 h-4" />
                        </div>
                    </Link>
                </motion.div>
            </div>
        </section>
    );
}

// ============================================================================
// FINAL CTA
// ============================================================================

function FinalCTA() {
    return (
        <section className="py-24 md:py-40 relative overflow-hidden">
            <div className="absolute inset-0 bg-piggy-radial" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-piggy-pink-500/10 rounded-full blur-3xl animate-pulse-glow" />

            <motion.div
                {...fadeUp}
                className="relative max-w-3xl mx-auto px-6 text-center"
            >
                <h2 className="display text-5xl md:text-7xl font-black tracking-tighter mb-6 leading-[0.95]">
                    Welcome to the
                    <br />
                    <span className="gradient-text">Hood.</span>
                </h2>
                <p className="text-piggy-cream/60 text-lg mb-10 max-w-xl mx-auto">
                    Non-custodial. On-chain. Algorithmically governed by a 48-hour timelock.
                    No team wallet, no presale, no promises — just six paths to earn ETH
                    while the supply burns down.
                </p>
                <div className="flex flex-wrap justify-center gap-3">
                    <Link to="/swap" className="btn-primary group">
                        Get PIGGY
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                    </Link>
                    <Link to="/hall" className="btn-ghost">
                        Verify the contracts
                    </Link>
                </div>
            </motion.div>
        </section>
    );
}
